import CarrierSelector from '@/common/components/CarrierSelector/CarrierSelector'
import CategoriesSelector from '@/common/components/CategoriesSelector/CategoriesSelector'
import DateRangeFilter from '@/common/components/DateRangeFilter/DateRangeFilter'
import DeliveryMethodTypeSelector from '@/common/components/DeliveryMethodTypeSelector/DeliveryMethodTypeSelector'
import { createEmptyFilter } from '@/common/components/FiltersDrawer/helpers'
import { displayMatchTypes } from '@/common/components/MatchTypeEditor/constants'
import { isRangeInvalid } from '@/common/components/MatchTypeEditor/helpers'
import RegionFilter from '@/common/components/RegionFilter/RegionFilter'
import { subscriptionPlans } from '@/common/constants'
import { providers } from '@/returns/constants'

import { filterNames } from '../constants'
import AlertTypesSelector from './AlertTypesSelector/AlertTypesSelector'
import AmountFilter from './AmountFilter/AmountFilter'
import { getAmountMatchType, getAmountParamsForMatchType, getSingleValueFilterName } from './AmountFilter/helpers'
import CollectionsSelector from './CollectionsSelector/CollectionsSelector'
import DeliveryMethodSelector from './DeliveryMethodSelector/DeliveryMethodSelector'
import ExchangedTypeSelector from './ExchangedTypeSelector/ExchangedTypeSelector'
import ExchangeForOrderStatusFilter from './ExchangeForOrderStatusFilter/ExchangeForOrderStatusFilter'
import DaysToValidateFilter from './ExpiresInFilter/ExpiresInFilter'
import ForReviewReasonFilter from './ForReviewReasonFilter/ForReviewReasonFilter'
import HasAlertsSelector from './HasAlertsSelector/HasAlertsSelector'
import HasPolicyRulesSelector from './HasPolicyRulesSelector/HasPolicyRulesSelector'
import InstantFilter from './InstantFilter/InstantFilter'
import IsGiftFilter from './IsGiftFilter/IsGiftFilter'
import LastUpdateFilter from './LastUpdateFilter/LastUpdateFilter'
import MatchModeSelector from './MatchModeSelector/MatchModeSelector'
import OrderTypeSelector from './OrderTypeSelector/OrderTypeSelector'
import PaymentStatusSelector from './PaymentStatusSelector/PaymentStatusSelector'
import PolicyRulesSelector from './PolicyRulesSelector/PolicyRulesSelector'
import ProductTypesSelector from './ProductTypesSelector/ProductTypesSelector'
import RefundMethodSelector from './RefundMethodSelector/RefundMethodSelector'
import RefundStatusSelector from './RefundStatusSelector/RefundStatusSelector'
import RejectReasonFilter from './RejectReasonFilter/RejectReasonFilter'
import { filterLevels } from './ReturnReasonFilter/constants'
import ReturnReasonFilter from './ReturnReasonFilter/ReturnReasonFilter'
import ShippingLabelStatusSelector from './ShippingLabelStatusSelector/ShippingLabelStatusSelector'
import StatusSelector from './StatusSelector/StatusSelector'
import TransactionTagsFilter from './TransactionTagsFilter/TransactionTagsFilter'
import TransactionTypeSelector from './TransactionTypeSelector/TransactionTypeSelector'
import VariantSkuFilterField from './VariantSkuFilterField/VariantSkuFilterField'
import {
  amountBoundKeys,
  amountFilterConfig,
  amountFilterNames,
  filterTypes,
  matchModeFilterNames,
  matchModes,
} from './constants'

const arrayFilterNames = [
  filterNames.status,
  filterNames.transactionType,
  filterNames.deliveryMethodType,
  filterNames.refundMethod,
  filterNames.returnReason,
  filterNames.rejectReason,
  filterNames.forReviewReason,
  filterNames.region,
  filterNames.variantsSkus,
  filterNames.exchangedType,
  filterNames.productCategory,
  filterNames.productCollection,
  filterNames.productType,
  filterNames.carrier,
]

const shopifyOnlyFilters = [filterNames.productCollection, filterNames.productType]

export const dateFilterConfig = {
  [filterNames.refundDate]: {
    dateRange: 'refundDates',
    dateStart: filterNames.refundDateStart,
    dateEnd: filterNames.refundDateEnd,
    daysGte: filterNames.refundDatesDaysGte,
    daysLte: filterNames.refundDatesDaysLte,
  },
  [filterNames.lastUpdate]: {
    dateRange: null,
    dateStart: null,
    dateEnd: null,
    daysGte: filterNames.lastUpdatedGte,
    daysLte: filterNames.lastUpdatedLte,
    extra: filterNames.lastUpdate,
  },
  [filterNames.exchangeCreatedDate]: {
    dateRange: 'exchangeCreatedDates',
    dateStart: filterNames.exchangeCreatedGte,
    dateEnd: filterNames.exchangeCreatedLte,
    daysGte: filterNames.exchangeCreatedDaysGte,
    daysLte: filterNames.exchangeCreatedDaysLte,
  },
  [filterNames.refundCreatedDate]: {
    dateRange: 'refundCreatedDates',
    dateStart: filterNames.refundCreatedGte,
    dateEnd: filterNames.refundCreatedLte,
    daysGte: filterNames.refundCreatedDaysGte,
    daysLte: filterNames.refundCreatedDaysLte,
  },
}

export const dateFilterNames = Object.keys(dateFilterConfig)

export const getDefaultFilterValue = type => {
  if (dateFilterNames.includes(type)) {
    const config = dateFilterConfig[type]
    const value = {}
    if (config.dateRange) value[config.dateRange] = null
    if (config.extra) value[config.extra] = null
    value[config.daysGte] = null
    value[config.daysLte] = null
    return value
  }

  if (amountFilterNames.includes(type)) {
    return getEmptyAmountValue(amountFilterConfig[type])
  }

  if (type === filterNames.returnReason) {
    return { returnReason: [], returnReasonFilterLevel: filterLevels.reasonType }
  }

  if (matchModeFilterNames[type]) {
    return { [type]: [], [matchModeFilterNames[type]]: matchModes.any }
  }

  if (arrayFilterNames.includes(type)) {
    return { [type]: [] }
  }

  return { [type]: '' }
}

const hasValue = value => value != null && value !== ''

export const getEmptyAmountValue = config => {
  return {
    [config.matchTypeName]: null,
    [config.eq]: null,
    [config.gt]: null,
    [config.lt]: null,
    [config.gte]: null,
    [config.lte]: null,
  }
}

export const clearAmountFilter = (config, onChangeFilter, doRequest) => {
  onChangeFilter(config.eq, null, false)
  onChangeFilter(config.gt, null, false)
  onChangeFilter(config.lt, null, false)
  onChangeFilter(config.gte, null, false)
  onChangeFilter(config.lte, null, doRequest)
}

const isAmountValid = value => hasValue(value) && Number.isFinite(Number(value))

export const isAmountFilterValid = (valueMap, config) => {
  const matchType = valueMap[config.matchTypeName]

  if (!matchType) return false

  if (matchType === displayMatchTypes.valueInRange) {
    if (!isAmountValid(valueMap[config.gte]) || !isAmountValid(valueMap[config.lte])) return false

    return !isRangeInvalid(valueMap[config.gte], valueMap[config.lte])
  }

  return isAmountValid(valueMap[getSingleValueFilterName(config, matchType)])
}

const isValueActive = (type, valueMap) => {
  if (dateFilterNames.includes(type)) {
    const config = dateFilterConfig[type]
    return Boolean(
      (config.dateRange &&
        (hasValue(valueMap[config.dateRange]?.startDate) || hasValue(valueMap[config.dateRange]?.endDate))) ||
      hasValue(valueMap[config.daysGte]) ||
      hasValue(valueMap[config.daysLte]) ||
      (config.extra && hasValue(valueMap[config.extra]))
    )
  }

  if (amountFilterNames.includes(type)) {
    return getAmountMatchType(valueMap, amountFilterConfig[type]) !== null
  }

  if (type === filterNames.returnReason) {
    const reasonValues = valueMap[filterNames.returnReason]
    const reasonTypeValues = valueMap[filterNames.returnReasonType]
    return reasonValues?.length > 0 || reasonTypeValues?.length > 0
  }

  const value = valueMap[type]
  if (Array.isArray(value)) return value.length > 0
  return hasValue(value)
}

const buildValueFromFilters = (type, filters) => {
  if (dateFilterNames.includes(type)) {
    const config = dateFilterConfig[type]
    const value = {}
    if (config.dateRange) value[config.dateRange] = filters[config.dateRange] || null
    if (config.extra) value[config.extra] = filters[config.extra] || null
    value[config.daysGte] = filters[config.daysGte] ?? null
    value[config.daysLte] = filters[config.daysLte] ?? null
    return value
  }

  if (amountFilterNames.includes(type)) {
    const config = amountFilterConfig[type]
    const matchType = getAmountMatchType(filters, config)

    return {
      ...getEmptyAmountValue(config),
      ...getAmountParamsForMatchType(filters, config, matchType),
      [config.matchTypeName]: matchType,
    }
  }

  if (type === filterNames.returnReason) {
    const reasonTypeValues = filters[filterNames.returnReasonType]
    if (reasonTypeValues?.length > 0) {
      return { returnReason: reasonTypeValues, returnReasonFilterLevel: filterLevels.reasonType }
    }
    return { returnReason: filters[filterNames.returnReason] || [], returnReasonFilterLevel: filterLevels.reason }
  }

  if (matchModeFilterNames[type]) {
    const matchModeName = matchModeFilterNames[type]

    return { [type]: filters[type], [matchModeName]: filters[matchModeName] || matchModes.any }
  }

  return { [type]: filters[type] }
}

export const getInitialFilters = filters => {
  const initialFilters = filterTypes
    .filter(ft => isValueActive(ft.value, filters))
    .map(ft => ({
      id: crypto.randomUUID(),
      type: ft.value,
      value: buildValueFromFilters(ft.value, filters),
    }))

  return initialFilters.length > 0 ? initialFilters : [createEmptyFilter()]
}

export const isFilterValid = filter => {
  if (!filter.type) return true
  if (filter.type === filterNames.returnReason) {
    return filter.value.returnReason?.length > 0
  }
  if (amountFilterNames.includes(filter.type)) {
    return isAmountFilterValid(filter.value, amountFilterConfig[filter.type])
  }
  return isValueActive(filter.type, filter.value)
}

export const getFilterLabel = filter => {
  if (filter.type === filterNames.returnReason) {
    return filter.value?.returnReasonFilterLevel === filterLevels.reason
      ? 'Motivo de devolución'
      : 'Tipo de motivo de devolución'
  }
  return filterTypes?.find(ft => ft.value === filter.type)?.label
}

const applyDateFilter = (value, config, onChangeFilter) => {
  if (config.dateRange) {
    onChangeFilter(config.dateStart, value[config.dateRange]?.startDate ?? null, false)
    onChangeFilter(config.dateEnd, value[config.dateRange]?.endDate ?? null, false)
  }

  if (config.extra) {
    onChangeFilter(config.extra, value[config.extra] ?? null, false)
  }

  onChangeFilter(config.daysGte, value[config.daysGte] ?? null, false)
  onChangeFilter(config.daysLte, value[config.daysLte] ?? null, false)
}

const applyAmountFilter = (value, config, onChangeFilter) => {
  amountBoundKeys.forEach(boundKey => {
    const filterName = config[boundKey]

    onChangeFilter(filterName, value[filterName] ?? null, false)
  })
}

const applyReturnReasonFilter = (value, onChangeFilter) => {
  const isReasonTypeLevel = value.returnReasonFilterLevel === filterLevels.reasonType
  const filterName = isReasonTypeLevel ? filterNames.returnReasonType : filterNames.returnReason

  onChangeFilter(filterName, value.returnReason, false)
}

const applyMatchModeFilter = (value, type, onChangeFilter) => {
  const matchModeName = matchModeFilterNames[type]

  onChangeFilter(type, value[type], false)
  onChangeFilter(matchModeName, value[matchModeName], false)
}

const applyFilter = ({ type, value }, onChangeFilter) => {
  if (dateFilterNames.includes(type)) {
    applyDateFilter(value, dateFilterConfig[type], onChangeFilter)
    return
  }

  if (amountFilterNames.includes(type)) {
    applyAmountFilter(value, amountFilterConfig[type], onChangeFilter)
    return
  }

  if (type === filterNames.returnReason) {
    applyReturnReasonFilter(value, onChangeFilter)
    return
  }

  if (matchModeFilterNames[type]) {
    applyMatchModeFilter(value, type, onChangeFilter)
    return
  }

  onChangeFilter(type, value[type], false)
}

export const applyFilters = (filters, onChangeFilter) => {
  filters.forEach(filter => applyFilter(filter, onChangeFilter))
}

export const filterFiltersBySubscriptionPlan = (filters, subscriptionPlan) => {
  const filtersToRemove = [filterNames.instant]

  if (subscriptionPlan === subscriptionPlans.basic) {
    return filters.filter(f => !filtersToRemove.includes(f.value))
  }

  return filters
}

export const filterFiltersByProvider = (filters, provider) => {
  let filteredFilters = filters

  if (provider) {
    if (![providers.shopify].includes(provider)) {
      filteredFilters = filteredFilters.filter(f => !shopifyOnlyFilters.includes(f.value))
    }

    if (![providers.shopify, providers.magento, providers.prestashop].includes(provider)) {
      filteredFilters = filteredFilters.filter(f => f.value !== filterNames.productCategory)
    }
  }

  return filteredFilters
}

const filterFiltersByAlertTypes = (filters, hasAlertTypes) => {
  if (hasAlertTypes) return filters

  return filters.filter(f => f.value !== filterNames.pendingAlertTypes)
}

export const getEligibleFilterTypes = ({ filterTypes, subscriptionPlan, provider, hasAlertTypes }) => {
  let result = filterFiltersBySubscriptionPlan(filterTypes, subscriptionPlan)
  result = filterFiltersByProvider(result, provider)
  result = filterFiltersByAlertTypes(result, hasAlertTypes)

  return result
}

export const filterFields = {
  [filterNames.transactionType]: ({ value, onChangeValue }) => (
    <TransactionTypeSelector value={value.transactionType} onChange={v => onChangeValue({ transactionType: v })} />
  ),
  [filterNames.status]: ({ value, onChangeValue }) => (
    <StatusSelector value={value.status} onChange={v => onChangeValue({ status: v })} />
  ),
  [filterNames.deliveryMethodType]: ({ value, onChangeValue }) => (
    <DeliveryMethodTypeSelector
      value={value.deliveryMethodType}
      onChange={v => onChangeValue({ deliveryMethodType: v })}
    />
  ),
  [filterNames.deliveryMethod]: ({ value, onChangeValue, fieldProps }) => (
    <DeliveryMethodSelector
      value={value.deliveryMethod}
      onChange={v => onChangeValue({ deliveryMethod: v })}
      deliveryMethods={fieldProps.deliveryMethods}
    />
  ),
  [filterNames.refundDate]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.refundDates,
        dynamicValue: { daysGte: value.refundDatesDaysGte, daysLte: value.refundDatesDaysLte },
      }}
      onChangeRange={v => onChangeValue({ refundDates: v })}
      onChangeDaysGte={v => onChangeValue({ refundDatesDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ refundDatesDaysLte: v })}
      showTypeSelector
    />
  ),
  [filterNames.lastUpdate]: ({ value, onChangeValue }) => (
    <LastUpdateFilter
      lastUpdatedGteValue={value.lastUpdatedGte}
      lastUpdatedLteValue={value.lastUpdatedLte}
      onChangeLastUpdatedGte={v => onChangeValue({ lastUpdatedGte: v })}
      onChangeLastUpdatedLte={v => onChangeValue({ lastUpdatedLte: v })}
    />
  ),
  [filterNames.expiresIn]: ({ value, onChangeValue }) => (
    <DaysToValidateFilter value={value.expiresIn} onChange={v => onChangeValue({ expiresIn: v })} />
  ),
  [filterNames.refundMethod]: ({ value, onChangeValue, fieldProps }) => (
    <RefundMethodSelector
      value={value.refundMethod}
      onChange={v => onChangeValue({ refundMethod: v })}
      provider={fieldProps.provider}
    />
  ),
  [filterNames.returnReason]: ({ value, onChangeValue, fieldProps }) => (
    <ReturnReasonFilter
      returnReasons={fieldProps.returnReasons}
      value={value.returnReason}
      onChange={v => onChangeValue({ returnReason: v })}
      filterLevel={value.returnReasonFilterLevel}
      onChangeFilterLevel={level => onChangeValue({ returnReasonFilterLevel: level, returnReason: [] })}
    />
  ),
  [filterNames.rejectReason]: ({ value, onChangeValue, fieldProps }) => (
    <RejectReasonFilter
      rejectReasons={fieldProps.rejectReasons}
      value={value.rejectReason}
      onChange={v => onChangeValue({ rejectReason: v })}
    />
  ),
  [filterNames.forReviewReason]: ({ value, onChangeValue, fieldProps }) => (
    <ForReviewReasonFilter
      forReviewReasons={fieldProps.forReviewReasons}
      value={value.forReviewReason}
      onChange={v => onChangeValue({ forReviewReason: v })}
    />
  ),
  [filterNames.region]: ({ value, onChangeValue, fieldProps }) => (
    <RegionFilter regions={fieldProps.regions} value={value.region} onChange={v => onChangeValue({ region: v })} />
  ),
  [filterNames.instant]: ({ value, onChangeValue }) => (
    <InstantFilter value={value.instant} onChange={v => onChangeValue({ instant: v })} />
  ),
  [filterNames.exchangeForOrderStatus]: ({ value, onChangeValue }) => (
    <ExchangeForOrderStatusFilter
      value={value.exchangeForOrderStatus}
      onChange={v => onChangeValue({ exchangeForOrderStatus: v })}
    />
  ),
  [filterNames.paymentStatus]: ({ value, onChangeValue }) => (
    <PaymentStatusSelector value={value.paymentStatus} onChange={v => onChangeValue({ paymentStatus: v })} />
  ),
  [filterNames.refundStatus]: ({ value, onChangeValue }) => (
    <RefundStatusSelector value={value.refundStatus} onChange={v => onChangeValue({ refundStatus: v })} />
  ),
  [filterNames.variantsSkus]: ({ value, onChangeValue, fieldProps }) => (
    <VariantSkuFilterField
      value={value}
      onChangeValue={onChangeValue}
      isUnifiedDashboard={fieldProps.isUnifiedDashboard}
    />
  ),
  [filterNames.shippingLabelStatus]: ({ value, onChangeValue }) => (
    <ShippingLabelStatusSelector
      value={value.shippingLabelStatus}
      onChange={v => onChangeValue({ shippingLabelStatus: v })}
    />
  ),
  [filterNames.exchangeCreatedDate]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.exchangeCreatedDates,
        dynamicValue: { daysGte: value.exchangeCreatedDaysGte, daysLte: value.exchangeCreatedDaysLte },
      }}
      onChangeRange={v => onChangeValue({ exchangeCreatedDates: v })}
      onChangeDaysGte={v => onChangeValue({ exchangeCreatedDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ exchangeCreatedDaysLte: v })}
      showTypeSelector
    />
  ),
  [filterNames.hasAlerts]: ({ value, onChangeValue }) => (
    <HasAlertsSelector value={value.hasAlerts} onChange={v => onChangeValue({ hasAlerts: v })} />
  ),
  [filterNames.pendingAlertTypes]: ({ value, onChangeValue, fieldProps }) => {
    return (
      <>
        <MatchModeSelector
          selectedValues={value.pendingAlertTypes}
          value={value.pendingAlertTypesMatch}
          onChange={v => onChangeValue({ pendingAlertTypesMatch: v })}
        />
        <AlertTypesSelector
          value={value.pendingAlertTypes}
          availableAlertTypes={fieldProps.availableAlertTypes}
          loading={fieldProps.fetchingAlertTypes}
          onChange={v => onChangeValue({ pendingAlertTypes: v })}
        />
      </>
    )
  },
  [filterNames.exchangedType]: ({ value, onChangeValue }) => (
    <ExchangedTypeSelector value={value.exchangedType} onChange={v => onChangeValue({ exchangedType: v })} />
  ),
  [filterNames.refundCreatedDate]: ({ value, onChangeValue }) => (
    <DateRangeFilter
      value={{
        rangeValue: value.refundCreatedDates,
        dynamicValue: { daysGte: value.refundCreatedDaysGte, daysLte: value.refundCreatedDaysLte },
      }}
      onChangeRange={v => onChangeValue({ refundCreatedDates: v })}
      onChangeDaysGte={v => onChangeValue({ refundCreatedDaysGte: v })}
      onChangeDaysLte={v => onChangeValue({ refundCreatedDaysLte: v })}
      showTypeSelector
    />
  ),
  [filterNames.productCategory]: ({ value, onChangeValue, fieldProps }) => (
    <CategoriesSelector
      categories={fieldProps.categories}
      value={value.productCategory || []}
      onChange={v => onChangeValue({ productCategory: v })}
    />
  ),
  [filterNames.productCollection]: ({ value, onChangeValue, fieldProps }) => (
    <CollectionsSelector
      collections={fieldProps.collections}
      value={value.productCollection || []}
      onChange={v => onChangeValue({ productCollection: v })}
    />
  ),
  [filterNames.productType]: ({ value, onChangeValue, fieldProps }) => (
    <ProductTypesSelector
      productTypes={fieldProps.productTypes}
      value={value.productType || []}
      onChange={v => onChangeValue({ productType: v })}
    />
  ),
  [filterNames.carrier]: ({ value, onChangeValue, fieldProps }) => (
    <CarrierSelector
      value={value.carrier || []}
      onChange={v => onChangeValue({ carrier: v })}
      carriers={fieldProps.carriers}
      multiple
      label={fieldProps.t('Transportista devolución')}
      showIcon={false}
      showLogo={false}
    />
  ),
  [filterNames.orderType]: ({ value, onChangeValue }) => (
    <OrderTypeSelector value={value.orderType} onChange={v => onChangeValue({ orderType: v })} />
  ),
  [filterNames.isGift]: ({ value, onChangeValue }) => (
    <IsGiftFilter value={value.isGift} onChange={v => onChangeValue({ isGift: v })} />
  ),
  [filterNames.transactionTags]: ({ value, onChangeValue }) => {
    return (
      <>
        <MatchModeSelector
          selectedValues={value.transactionTags}
          value={value.transactionTagsMatch}
          onChange={v => onChangeValue({ transactionTagsMatch: v })}
        />
        <TransactionTagsFilter value={value.transactionTags} onChange={v => onChangeValue({ transactionTags: v })} />
      </>
    )
  },
  [filterNames.policyRules]: ({ value, onChangeValue, fieldProps }) => {
    return (
      <>
        <MatchModeSelector
          selectedValues={value.policyRules}
          value={value.policyRulesMatch}
          onChange={v => onChangeValue({ policyRulesMatch: v })}
        />
        <PolicyRulesSelector
          policyRules={fieldProps.policyRules}
          value={value.policyRules}
          onChange={v => onChangeValue({ policyRules: v })}
        />
      </>
    )
  },
  [filterNames.hasPolicyRules]: ({ value, onChangeValue }) => (
    <HasPolicyRulesSelector value={value.hasPolicyRules} onChange={v => onChangeValue({ hasPolicyRules: v })} />
  ),
  [filterNames.refundTotalAmount]: ({ value, onChangeValue }) => {
    return (
      <AmountFilter
        filterConfig={amountFilterConfig[filterNames.refundTotalAmount]}
        value={value}
        onChangeValue={onChangeValue}
      />
    )
  },
  [filterNames.paymentTotalAmount]: ({ value, onChangeValue }) => {
    return (
      <AmountFilter
        filterConfig={amountFilterConfig[filterNames.paymentTotalAmount]}
        value={value}
        onChangeValue={onChangeValue}
      />
    )
  },
}
